import Image from 'next/image';
import { useMemo } from 'react';
import { NETWORKS } from 'src/common/types/network';

import { Badge, KeyValueRow, KeyValueTable } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { HypurrEulerNetPositionsStrategy, Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function HypurrEulerNetPositionsTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();

  const depositTokenAddress = useMemo(() => {
    return trigger.context?.depositTokenAddress || '';
  }, [trigger.context]);

  const debtTokenAddress = useMemo(() => {
    return trigger.context?.debtTokenAddress || '';
  }, [trigger.context]);

  const volumeCurrencyAddress = useMemo(() => {
    return trigger.context?.volumeCurrencyAddress || '';
  }, [trigger.context]);

  const chainId = useMemo(() => {
    return trigger.context?.chainId || 999;
  }, [trigger.context]);

  const strategy = useMemo(() => {
    return trigger.context?.strategy;
  }, [trigger.context]);

  const networkInfo = useMemo(() => {
    return NETWORKS.find((network) => network.chainId === chainId);
  }, [chainId]);

  const networkDisplay = useMemo(() => {
    if (!networkInfo) return '-';
    const chainIconUrl = getChainIconUrl(networkInfo.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={networkInfo.name} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{networkInfo.name.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [networkInfo, getChainIconUrl]);

  const depositTokenDisplay = useMemo(() => {
    if (!depositTokenAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <AddressChip address={depositTokenAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [depositTokenAddress, networkInfo]);

  const debtTokenDisplay = useMemo(() => {
    if (!debtTokenAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <AddressChip address={debtTokenAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [debtTokenAddress, networkInfo]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!volumeCurrencyAddress || !networkInfo) return '-';

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <CurrencyDisplay value={volumeCurrencyAddress} chainId={networkInfo.chainId} />
      </div>
    );
  }, [volumeCurrencyAddress, networkInfo]);

  const strategyDisplay = useMemo(() => {
    if (!strategy) return '-';

    const color = strategy === HypurrEulerNetPositionsStrategy.Deposit ? 'success' : 'warning';
    const label = strategy === HypurrEulerNetPositionsStrategy.Deposit ? 'Deposit' : 'Borrow';

    return <Badge variant={color}>{label}</Badge>;
  }, [strategy]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Strategy" value={strategyDisplay} />
      <KeyValueRow label="Deposit Token" value={depositTokenDisplay} />
      <KeyValueRow label="Debt Token" value={debtTokenDisplay} />
      <KeyValueRow label="Volume Currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
